import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Bug,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Filter,
  ChevronDown,
  Layers
} from 'lucide-react';

import StatCard from '../../components/Cards/StatCard';
import { useStarterPlan } from "../../contexts/OrganizationPlanContext";

export default function BugOptimizer({ darkMode }) {

  const navigate = useNavigate();
  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const starterBypass = useStarterPlan();
  const canCreateBugs =
    starterBypass ||
    (currentUser?.role !== "org_admin" && selectedProject?.user_role === "qa");

  const [bugs, setBugs] = useState([]);
  const [filteredBugs, setFilteredBugs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter States
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState("All");
  const [moduleFilter, setModuleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  // Dropdown UI State
  const [openDropdown, setOpenDropdown] = useState(null); 

  const borderColor = darkMode ? 'border-gray-800/50' : 'border-gray-200';
  const cardBg = darkMode ? 'bg-[#161922]' : 'bg-white';
  const itemBg = darkMode ? 'bg-[#0f111a]' : 'bg-gray-50';

  // Fetch Bugs
  useEffect(() => {
    fetchBugs();
  }, [selectedProject]);

  const fetchBugs = async () => {
    try {
      setLoading(true);

      const url = selectedProject
        ? `https://api.testoraglobal.in/api/bug/project/${selectedProject.project_id}`
        : "https://api.testoraglobal.in/api/bug/all";

      const res = await axios.get(url);

      setBugs(res.data);
      setFilteredBugs(res.data);

    } catch (error) {
      console.error("Error fetching bugs", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Dynamic Option Extractors
  const availableSeverities = ["All", ...Array.from(new Set(bugs.map((b) => b.severity).filter(Boolean)))];
  const availableModules = ["All", ...Array.from(new Set(bugs.map((b) => b.module).filter(Boolean)))];
  const availableStatuses = ["All", ...Array.from(new Set(bugs.map((b) => b.status).filter(Boolean)))];

  const assigneeText = (bug) => {
    const names = bug.assigned_to_names || bug.assigned_to_label || [];
    if (Array.isArray(names) && names.length) return names.join(", ");
    if (typeof names === "string" && names.trim()) return names;

    const raw = bug.assigned_to;
    if (Array.isArray(raw)) return raw.join(", ");
    if (typeof raw === "string" && raw.trim()) return raw;
    return "Unassigned";
  };

  // ✅ Search & Filters Logic
  useEffect(() => {
    const filtered = bugs.filter((bug) => {
      // ✅ Updated Search logic to include ID, Title, Module, and Assignee
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        bug.title?.toLowerCase().includes(searchLower) ||
        bug.bug_id?.toString().toLowerCase().includes(searchLower) ||
        assigneeText(bug).toLowerCase().includes(searchLower) ||
        bug.module?.toLowerCase().includes(searchLower);

      // Dropdowns
      const matchesSeverity = severityFilter === "All" || bug.severity === severityFilter;
      const matchesModule = moduleFilter === "All" || bug.module === moduleFilter;
      const matchesStatus = statusFilter === "All" || bug.status === statusFilter;

      return matchesSearch && matchesSeverity && matchesModule && matchesStatus;
    });

    setFilteredBugs(filtered);

  }, [search, severityFilter, moduleFilter, statusFilter, bugs]);

  // Row Click
  const handleRowClick = (bug) => {
    navigate(`/bug-optimizer/view/${bug.bug_id}`);
  };

  const handleCreate = () => {
    navigate('/bug-optimizer/createbug');
  };

  return (

    <div className="px-8 pb-8 max-w-7xl mx-auto animate-in fade-in duration-500">

      {/* Header */}
      <div className="flex justify-between items-end mb-8">

        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            Bug Optimizer
          </h2>

          <p className="text-gray-500 text-sm mt-1">
            AI-powered bug tracking and optimization
          </p>
        </div>

        {canCreateBugs && (
          <button
            onClick={handleCreate}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold shadow-lg shadow-purple-500/20"
          >
            <Plus size={18} />
            Report Bug
          </button>
        )}

      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">

        <StatCard
          title="Total Bugs"
          value={bugs.length}
          icon={<Bug size={18} />}
          color="blue"
          darkMode={darkMode}
        />

        <StatCard
          title="Critical"
          value={bugs.filter(b => b.severity === "Critical").length}
          icon={<AlertTriangle size={18} />}
          color="red"
          darkMode={darkMode}
        />

        <StatCard
          title="Resolved"
          value={bugs.filter(b => b.status === "closed" || b.status === "resolved").length}
          icon={<CheckCircle size={18} />}
          color="green"
          darkMode={darkMode}
        />

        <StatCard
          title="Open"
          value={bugs.filter(b => b.status === "open").length}
          icon={<Calendar size={18} />}
          color="orange"
          darkMode={darkMode}
        />

      </div>

      {/* ✅ CONTROL PANEL: Search & Filters */}
      <div className={`p-4 rounded-2xl border ${cardBg} ${borderColor} mb-6 flex gap-4 items-center relative z-20`}>
        
        {/* Transparent Overlay to close dropdowns when clicking outside */}
        {openDropdown && (
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setOpenDropdown(null)} 
          />
        )}

        {/* SEARCH BOX */}
        <div className={`w-96 flex items-center px-4 py-2 rounded-xl ${itemBg} border ${borderColor} relative z-20`}>
          <Search size={18} className="text-gray-500 mr-2 shrink-0" />
          <input
            type="text"
            placeholder="Search by ID, Title, or Assignee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent border-none outline-none text-sm w-full text-gray-700 dark:text-gray-200"
          />
        </div>

        {/* Spacer to push dropdowns to the right */}
        <div className="flex-1"></div>

        {/* SEVERITY FILTER DROPDOWN */}
        <div className="relative z-20">
          <div
            onClick={() => setOpenDropdown(openDropdown === 'severity' ? null : 'severity')}
            className={`flex items-center px-4 py-2 rounded-xl ${itemBg} border ${borderColor} cursor-pointer hover:border-purple-500/50 transition-colors select-none`}
          >
            <AlertTriangle size={16} className="text-purple-500 mr-2 shrink-0" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mr-3 capitalize">
              {severityFilter === "All" ? "All Severities" : severityFilter}
            </span>
            <ChevronDown size={14} className="text-gray-500 shrink-0" />
          </div>

          {openDropdown === 'severity' && (
            <div 
              className={`absolute top-full left-0 mt-2 w-48 overflow-y-auto custom-scrollbar rounded-xl border ${borderColor} ${cardBg} shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 z-50`}
              style={{ maxHeight: "220px" }}
            >
              <div className="flex flex-col py-1">
                {availableSeverities.map(severity => (
                  <div
                    key={severity}
                    onClick={() => { setSeverityFilter(severity); setOpenDropdown(null); }}
                    className={`px-4 py-3 text-sm cursor-pointer transition-colors capitalize ${severityFilter === severity ? 'text-purple-500 bg-purple-500/10 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  >
                    {severity === "All" ? "All Severities" : severity}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* MODULE FILTER DROPDOWN */}
        <div className="relative z-20">
          <div
            onClick={() => setOpenDropdown(openDropdown === 'module' ? null : 'module')}
            className={`flex items-center px-4 py-2 rounded-xl ${itemBg} border ${borderColor} cursor-pointer hover:border-purple-500/50 transition-colors select-none`}
          >
            <Layers size={16} className="text-purple-500 mr-2 shrink-0" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mr-3 truncate max-w-[120px]">
              {moduleFilter === "All" ? "All Modules" : moduleFilter}
            </span>
            <ChevronDown size={14} className="text-gray-500 shrink-0" />
          </div>

          {openDropdown === 'module' && (
            <div 
              className={`absolute top-full left-0 mt-2 w-56 overflow-y-auto custom-scrollbar rounded-xl border ${borderColor} ${cardBg} shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 z-50`}
              style={{ maxHeight: "220px" }}
            >
              <div className="flex flex-col py-1">
                {availableModules.map(module => (
                  <div
                    key={module}
                    onClick={() => { setModuleFilter(module); setOpenDropdown(null); }}
                    className={`px-4 py-3 text-sm cursor-pointer transition-colors break-words ${moduleFilter === module ? 'text-purple-500 bg-purple-500/10 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  >
                    {module === "All" ? "All Modules" : module}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* STATUS FILTER DROPDOWN */}
        <div className="relative z-20">
          <div
            onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
            className={`flex items-center px-4 py-2 rounded-xl ${itemBg} border ${borderColor} cursor-pointer hover:border-purple-500/50 transition-colors select-none`}
          >
            <Filter size={16} className="text-purple-500 mr-2 shrink-0" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mr-3 capitalize">
              {statusFilter === "All" ? "All Statuses" : statusFilter}
            </span>
            <ChevronDown size={14} className="text-gray-500 shrink-0" />
          </div>

          {openDropdown === 'status' && (
            <div 
              className={`absolute top-full right-0 mt-2 w-48 overflow-y-auto custom-scrollbar rounded-xl border ${borderColor} ${cardBg} shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 z-50`}
              style={{ maxHeight: "220px" }}
            >
              <div className="flex flex-col py-1">
                {availableStatuses.map(status => (
                  <div
                    key={status}
                    onClick={() => { setStatusFilter(status); setOpenDropdown(null); }}
                    className={`px-4 py-3 text-sm cursor-pointer transition-colors capitalize ${statusFilter === status ? 'text-purple-500 bg-purple-500/10 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  >
                    {status === "All" ? "All Statuses" : status}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg} ${borderColor} relative z-0`}>

        <table className="w-full text-left">

          <thead>

            <tr className={`text-[10px] font-bold uppercase tracking-widest text-gray-500 border-b ${borderColor}`}>

              <th className="px-6 py-4">Bug ID</th>
              <th className="px-6 py-4">Jira Ticket</th>
              <th className="px-6 py-4">Title</th>
              <th className="px-6 py-4">Severity</th>
              <th className="px-6 py-4">Priority</th>
              <th className="px-6 py-4">Module</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Assignee</th>
              <th className="px-6 py-4">Created Date</th>

            </tr>

          </thead>

          <tbody className={`${darkMode ? 'divide-gray-800/30' : 'divide-gray-100'}`}>

            {loading ? (
              <tr>
                <td colSpan="9" className="text-center py-12 text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : filteredBugs.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-12 text-gray-500">
                  No Bugs Found
                </td>
              </tr>
            ) : (

              filteredBugs.map((bug) => (

                <tr
                  key={bug.bug_id}
                  onClick={() => handleRowClick(bug)}
                  className={`cursor-pointer transition-all duration-200 ${
                    darkMode
                      ? 'hover:bg-white/5'
                      : 'hover:bg-gray-50'
                  }`}
                >

                  <td className="px-6 py-5 text-xs font-bold text-purple-500">
                    {bug.bug_id}
                  </td>

                  <td className="px-6 py-5 text-xs">
                    {bug.jira_ticket_id && bug.jira_ticket_url ? (
                      <a href={bug.jira_ticket_url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="font-semibold text-blue-500 hover:underline">
                        {bug.jira_ticket_id}
                      </a>
                    ) : "—"}
                  </td>

                  <td className="px-6 py-5 text-xs font-bold">
                    {bug.title}
                  </td>

                  <td className="px-6 py-5">

                    <span
                      className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase ${
                        bug.severity === 'Critical'
                          ? 'text-red-500 bg-red-500/10'
                          : bug.severity === 'High'
                          ? 'text-orange-500 bg-orange-500/10'
                          : 'text-blue-500 bg-blue-500/10'
                      }`}
                    >
                      {bug.severity}
                    </span>

                  </td>

                  <td className="px-6 py-5 text-xs">
                    {bug.priority}
                  </td>

                  <td className="px-6 py-5 text-xs">
                    {bug.module}
                  </td>

                  <td className="px-6 py-5">

                    <span
                      className={`text-xs capitalize font-medium ${
                        bug.status === 'closed' || bug.status === 'resolved'
                          ? 'text-green-500'
                          : 'text-orange-400'
                      }`}
                    >
                      {bug.status}
                    </span>

                  </td>

                  <td className="px-6 py-5 text-xs">
                    {assigneeText(bug)}
                  </td>

                  <td className="px-6 py-5 text-xs text-gray-500">
                    {new Date(bug.raised_date).toLocaleDateString()}
                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

    </div>

  );

}